import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalculatorInputs } from '../components/CalculatorInputs';
import { AssumptionsSidebar } from '../components/AssumptionsSidebar';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { ShareSummary } from '../components/ShareSummary';
import { HiringFunnelModal } from '../components/HiringFunnelModal';
import { ExpandableHiringFunnel } from '../components/ExpandableHiringFunnel';
import { Inputs, DEFAULT_INPUTS, calculateModel, PackageType, BuildPayType, IndustryType, optimizeCampaignsForRevenueTarget } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
const CORRECT_PASSWORD = 'HIRE100k';
const ADDITIONAL_PASSWORDS = ['mymanny', '1234', 'temp1234'];
const TEMP_PASSWORD = 'UNTD.solar';
const TEMP_PASSWORD_START = new Date('2026-02-02T00:00:00');
const TEMP_PASSWORD_END = new Date('2026-02-07T00:00:00'); // End of Feb 6th (midnight Feb 7th)
const TEMP_PASSWORD_2 = 'Polaris';
const TEMP_PASSWORD_2_START = new Date('2026-03-27T00:00:00');
const TEMP_PASSWORD_2_END = new Date('2026-03-30T00:00:00'); // End of Mar 29th (midnight Mar 30th)
const TEMP_PASSWORD_3 = '1234';
const TEMP_PASSWORD_3_START = new Date('2026-04-07T00:00:00');
const TEMP_PASSWORD_3_END = new Date('2026-04-23T00:00:00'); // End of Apr 22nd (midnight Apr 23rd)
const AUTH_KEY = 'hire_calculator_authenticated';
const isValidPassword = (password: string): boolean => {
  // Check permanent passwords
  if (password === CORRECT_PASSWORD) {
    return true;
  }
  // Check additional permanent passwords
  if (ADDITIONAL_PASSWORDS.includes(password)) {
    return true;
  }
  // Check temporary passwords within date range
  const now = new Date();
  if (password === TEMP_PASSWORD && now >= TEMP_PASSWORD_START && now < TEMP_PASSWORD_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_2 && now >= TEMP_PASSWORD_2_START && now < TEMP_PASSWORD_2_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_3 && now >= TEMP_PASSWORD_3_START && now < TEMP_PASSWORD_3_END) {
    return true;
  }
  return false;
};
export function ROICalculator() {
  // Set browser tab title
  useEffect(() => {
    document.title = 'Recruiting ROI Calculator';
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Shared state for toggles
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  const [onTarget, setOnTarget] = useState(false);
  const [preOnTargetValues, setPreOnTargetValues] = useState<{
    pctLaunched: number;
    pct30: number;
    pct90: number;
    pct1y: number;
    salesFirst90Total: number;
    salesVetMonthly: number;
  } | null>(null);
  const [plusFive, setPlusFive] = useState(false);
  const [prePlusFiveValues, setPrePlusFiveValues] = useState<{
    pctLaunched: number;
    pct30: number;
    pct90: number;
    pct1y: number;
    salesFirst90Total: number;
    salesVetMonthly: number;
  } | null>(null);
  const [preOptimizeMonthlyCampaigns, setPreOptimizeMonthlyCampaigns] = useState<number[] | null>(null);
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(6);
  const [copySummaryLabel, setCopySummaryLabel] = useState('Copy Summary');
  const [savePdfLabel, setSavePdfLabel] = useState('Save as PDF');
  const [showPackageTooltip, setShowPackageTooltip] = useState(false);
  const [showBuildTooltip, setShowBuildTooltip] = useState(false);
  const [expandedReviewSections, setExpandedReviewSections] = useState({
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModeling: false
  });
  const [showHiringFunnel, setShowHiringFunnel] = useState(false);
  const [showHiringMetrics, setShowHiringMetrics] = useState(false);
  const [showOverhead, setShowOverhead] = useState(false);
  // Overhead values state
  const [preInterviewHours, setPreInterviewHours] = useState(40);
  const [interviewToOnboardingHours, setInterviewToOnboardingHours] = useState(60);
  const [adminCost, setAdminCost] = useState(2000);
  const [leadershipCost, setLeadershipCost] = useState(3000);
  const resultsRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  // Check authentication on mount (client-side only)
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem(AUTH_KEY);
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Load state from URL on mount
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length === 0) return;
    const newInputs = {
      ...DEFAULT_INPUTS
    };
    const parseNum = (key: string, def: number) => {
      const val = params.get(key);
      return val ? parseFloat(val) : def;
    };
    if (params.has('pkg')) newInputs.pkg = params.get('pkg') as PackageType;
    if (params.has('buildPay')) newInputs.buildPay = params.get('buildPay') as BuildPayType;
    if (params.has('industry')) newInputs.industry = params.get('industry') as IndustryType;
    newInputs.campaigns = parseNum('campaigns', DEFAULT_INPUTS.campaigns);
    newInputs.adSpend = parseNum('adSpend', DEFAULT_INPUTS.adSpend);
    newInputs.cph = parseNum('cph', DEFAULT_INPUTS.cph);
    newInputs.ticket = parseNum('ticket', DEFAULT_INPUTS.ticket);
    newInputs.gpm = parseNum('gpm', DEFAULT_INPUTS.gpm);
    newInputs.salesVetMonthly = parseNum('salesVetMonthly', DEFAULT_INPUTS.salesVetMonthly);
    newInputs.salesFirst90Total = parseNum('salesFirst90Total', DEFAULT_INPUTS.salesFirst90Total);
    newInputs.pctLaunched = parseNum('pctLaunched', DEFAULT_INPUTS.pctLaunched);
    newInputs.pct30 = parseNum('pct30', DEFAULT_INPUTS.pct30);
    newInputs.pct90 = parseNum('pct90', DEFAULT_INPUTS.pct90);
    newInputs.pct1y = parseNum('pct1y', DEFAULT_INPUTS.pct1y);
    newInputs.pullthrough = parseNum('pullthrough', DEFAULT_INPUTS.pullthrough);
    // Initialize monthly campaigns based on base campaigns if not present
    newInputs.monthlyCampaigns = Array(12).fill(newInputs.campaigns);
    newInputs.monthlyAdSpend = Array(12).fill(newInputs.adSpend);
    setInputs(newInputs);
    if (params.has('calculated') && params.get('calculated') === 'true') {
      setHasCalculated(true);
    }
  }, [isClient]);
  // Update URL when inputs change
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams();
    Object.entries(inputs).forEach(([key, value]) => {
      if (key !== 'monthlyCampaigns' && key !== 'monthlyAdSpend') {
        // Don't clutter URL with arrays
        params.set(key, String(value));
      }
    });
    if (hasCalculated) {
      params.set('calculated', 'true');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [inputs, hasCalculated, isClient]);
  // Sync monthly campaigns when base campaigns change - ONE WAY ONLY
  useEffect(() => {
    // When base campaigns changes, ALWAYS update all monthly campaigns to match
    // This is a one-way sync: top → table only
    if (inputs.campaigns !== undefined) {
      // Only update if monthlyCampaigns doesn't exist or if all values match the old campaigns value
      // This allows manual edits in the table to persist
      const needsInit = !inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyCampaigns: Array(12).fill(inputs.campaigns)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyCampaigns.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current campaigns input,
        // update them all to match the new campaigns value
        if (allSame && inputs.monthlyCampaigns[0] !== inputs.campaigns) {
          setInputs(prev => ({
            ...prev,
            monthlyCampaigns: Array(12).fill(inputs.campaigns)
          }));
        }
      }
    }
  }, [inputs.campaigns]);
  // Sync monthly ad spend when base ad spend changes - ONE WAY ONLY
  useEffect(() => {
    if (inputs.adSpend !== undefined) {
      const needsInit = !inputs.monthlyAdSpend || inputs.monthlyAdSpend.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyAdSpend: Array(12).fill(inputs.adSpend)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyAdSpend.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current ad spend input,
        // update them all to match the new ad spend value
        if (allSame && inputs.monthlyAdSpend[0] !== inputs.adSpend) {
          setInputs(prev => ({
            ...prev,
            monthlyAdSpend: Array(12).fill(inputs.adSpend)
          }));
        }
      }
    }
  }, [inputs.adSpend]);
  // Initialize if missing
  if (!inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12) {
    setInputs(prev => ({
      ...prev,
      monthlyCampaigns: Array(12).fill(prev.campaigns)
    }));
  }
  // Calculate results - recalculates whenever inputs change (including monthlyCampaigns and monthlyAdSpend)
  const results = useMemo(() => {
    // Set default buildPay if not selected
    const inputsWithDefaults = {
      ...inputs,
      pkg: inputs.pkg,
      buildPay: inputs.buildPay || 'upfront'
    };
    return calculateModel(inputsWithDefaults);
  }, [inputs, inputs.monthlyCampaigns, inputs.monthlyAdSpend, inputs.pkg, inputs.buildPay]);
  const toggleReviewSection = (section: keyof typeof expandedReviewSections) => {
    setExpandedReviewSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidPassword(password)) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_KEY, 'true');
      setError('');
    } else {
      setError('Incorrect access code. Please try again.');
      setPassword('');
    }
  };
  const handleCalculate = () => {
    // Ensure monthly campaigns are initialized
    if (!inputs.monthlyCampaigns) {
      setInputs(prev => ({
        ...prev,
        monthlyCampaigns: Array(12).fill(prev.campaigns)
      }));
    }
    setHasCalculated(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReview = () => {
    setHasReviewed(true);
    setTimeout(() => {
      reviewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setHasCalculated(false);
    setHasReviewed(false);
    setHalfSales(false);
    setDoubleAttrition(false);
    setOnTarget(false);
    setPreOnTargetValues(null);
    setPlusFive(false);
    setPrePlusFiveValues(null);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleToggleOnTarget = () => {
    if (!onTarget) {
      // Save current values before overriding
      setPreOnTargetValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      // Apply "On Target" values
      setInputs(prev => ({
        ...prev,
        pctLaunched: 80,
        pct30: 40,
        pct90: 80,
        pct1y: 40,
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.1 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.1 * 10) / 10
      }));
      setOnTarget(true);
    } else {
      // Restore saved values
      if (preOnTargetValues) {
        setInputs(prev => ({
          ...prev,
          ...preOnTargetValues
        }));
      }
      setOnTarget(false);
      setPreOnTargetValues(null);
    }
  };
  const handlePlusFive = () => {
    if (!plusFive) {
      // Save current values before applying +5%
      setPrePlusFiveValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      setInputs(prev => ({
        ...prev,
        pctLaunched: Math.min(100, (prev.pctLaunched as number) + 5),
        pct30: Math.min(100, (prev.pct30 as number) + 5),
        pct90: Math.min(100, (prev.pct90 as number) + 5),
        pct1y: Math.min(100, (prev.pct1y as number) + 5),
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.05 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.05 * 10) / 10
      }));
      setPlusFive(true);
    } else {
      // Restore saved values
      if (prePlusFiveValues) {
        setInputs(prev => ({
          ...prev,
          ...prePlusFiveValues
        }));
      }
      setPlusFive(false);
      setPrePlusFiveValues(null);
    }
  };
  const handleOptimizeForTarget = (target: number, months: 3 | 6 | 12 = timeframe) => {
    if (!target || target <= 0) return;
    const stash = inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12 ? [...inputs.monthlyCampaigns] : Array(12).fill(inputs.campaigns);
    const pattern = optimizeCampaignsForRevenueTarget(inputs, target, months);
    if (pattern) {
      setPreOptimizeMonthlyCampaigns(stash);
      setInputs({
        ...inputs,
        monthlyCampaigns: pattern,
        campaigns: pattern[0],
        revenueTarget: target
      });
    }
  };
  const handleUndoOptimize = () => {
    if (preOptimizeMonthlyCampaigns) {
      setInputs({
        ...inputs,
        monthlyCampaigns: preOptimizeMonthlyCampaigns,
        campaigns: preOptimizeMonthlyCampaigns[0]
      });
      setPreOptimizeMonthlyCampaigns(null);
    }
  };
  // Handle numeric input changes for Review section
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    if (value === '') {
      setInputs({
        ...inputs,
        [field]: ''
      });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setInputs({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const handleCopySummary = async () => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      try {
        await navigator.clipboard.writeText(textarea.value);
        setCopySummaryLabel('Copied ✓');
        setTimeout(() => setCopySummaryLabel('Copy Summary'), 1200);
      } catch (err) {
        console.error('Failed to copy', err);
      }
    }
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLinkLabel('Link Copied✓');
      setTimeout(() => setCopyLinkLabel('Copy Share Link'), 1200);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };
  const handleSavePdf = async () => {
    setSavePdfLabel('Generating PDF...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const element = resultsRef.current;
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const companyName = inputs.companyName || 'Company';
      const fileName = `${companyName.replace(/\s+/g, '_')}_ROI_Projection.pdf`;
      pdf.save(fileName);
      setSavePdfLabel('PDF Saved ✓');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      setSavePdfLabel('Failed - Try Again');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    }
  };
  // Calculated values - REVERSED RETENTION FUNNEL
  // Start with 1 rep at Active 1 Year and calculate upward
  const targetActive1Year = 1; // Fixed: 1 rep who stays long-term
  // Work backwards through retention rates to find how many hires needed
  const reverseActive90 = targetActive1Year / (inputs.pct1y / 100);
  const reverseActive30 = reverseActive90 / (inputs.pct90 / 100);
  const reverseLaunched = reverseActive30 / (inputs.pct30 / 100);
  const reverseHires = reverseLaunched / (inputs.pctLaunched / 100);
  // Keep the original forward calculations for other uses
  const adSpendTotal = inputs.adSpend * inputs.campaigns;
  const estimatedHires = Math.round(adSpendTotal / inputs.cph);
  const estimatedLaunched = estimatedHires * (inputs.pctLaunched / 100);
  const estimatedActive30 = estimatedLaunched * (inputs.pct30 / 100);
  const estimatedActive90 = estimatedActive30 * (inputs.pct90 / 100);
  const estimatedActive1Year = estimatedActive90 * (inputs.pct1y / 100);
  const gpPerSale = inputs.ticket * (inputs.gpm / 100);
  const rookieTotalSales = inputs.salesFirst90Total;
  // Round rep counts to nearest tenth for clean display
  const roundedActive90 = Math.round(estimatedActive90 * 10) / 10;
  const roundedActive1Year = Math.round(estimatedActive1Year * 10) / 10;
  const rookieMonthlyRevenue = roundedActive90 * rookieTotalSales * inputs.ticket * (inputs.pullthrough / 100) / 3;
  const veteranMonthlyRevenue = roundedActive1Year * inputs.salesVetMonthly * inputs.ticket * (inputs.pullthrough / 100);
  const canCalculate = true; // Allow calculation even without package selected
  // Show loading during initial mount to prevent hydration mismatch
  if (!isClient) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-516" data-mp="2ezx7oqav0dzk"></div>;
  }
  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4" data-id="element-517" data-mp="2sx2b0ij6uyr4">
        <div className="w-full max-w-md" data-id="element-518" data-mp="32p8bkgniaohx">
          <div className="text-center mb-8" data-id="element-519" data-mp="32nwzltjwqafq">
            <img src="https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png" alt="HIRE" className="h-12 mx-auto mb-6" data-id="element-520" data-mp="32r2s7irqvug7" />
            <h1 className="text-2xl font-bold text-navy mb-2" data-id="element-521" data-mp="37ob9lh52juge">
              ROI Calculator Access
            </h1>
            <p className="text-gray-600" data-id="element-522" data-mp="3k6t0e48wyds1">Enter your access code to continue</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-lg" data-id="element-523" data-mp="3f8860lgceobi">
            <div className="mb-6" data-id="element-524" data-mp="3t77kj0a55mty">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-525" data-mp="3o8mv7vwfgnka">
                Access Code
              </label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors font-mono" placeholder="Enter code" autoFocus data-id="element-526" data-mp="30gpmido9ybne" />
              {error && <p className="mt-2 text-sm text-red-600" data-id="element-527" data-mp="2cu0zjdkerszx">{error}</p>}
            </div>

            <button type="submit" className="w-full py-3 rounded bg-amber hover:bg-amber-dark text-navy font-bold text-base transition-colors shadow-md" data-id="element-528" data-mp="sbp0ximiqp0k">
              Access Calculator
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6" data-id="element-529" data-mp="jclgaa7pe1jy">
            Your access will be saved for future visits
          </p>
        </div>
      </div>;
  }
  // Main calculator content
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-530" data-mp="1bxbkdppfq9g9">
      {/* Floating Edit Button - Always visible */}
      <button onClick={() => setIsSidebarOpen(true)} className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-amber hover:bg-amber-dark text-navy shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95" title="Edit Assumptions" data-id="element-531" data-mp="121iayf70iw2p">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-532" data-mp="3rqahlayoqrlf">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-533" data-mp="33ne0v7a0ye7e" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-534" data-mp="2yv43j66kybv3" />
        </svg>
      </button>

      {/* Assumptions Sidebar */}
      <AssumptionsSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} inputs={inputs} onChange={setInputs} data-id="element-535" data-mp="2tuygy4f63fjv" />

      {/* Header with Logo */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-536" data-mp="2k0zknm1rypzu">
        <div className="max-w-[1920px] mx-auto px-4 py-4 md:py-6 flex items-center justify-between" data-id="element-537" data-mp="2fybbfd95fh2c">
          <img src="https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png" alt="HIRE" className="h-8 md:h-12" data-id="element-538" data-mp="2azi5c5w6ysc7" />
          <a href="https://deck.thehireadvantage.co" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold text-sm transition-colors shadow-md" data-id="element-539" data-mp="96k4bms9gtg6">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-540" data-mp="qh7c1kgzflws">
              <rect x="3" y="3" width="7" height="7" data-id="element-541" data-mp="2q1yxd2ff7q6" />
              <rect x="14" y="3" width="7" height="7" data-id="element-542" data-mp="2so4561uhb58" />
              <rect x="14" y="14" width="7" height="7" data-id="element-543" data-mp="7olix7xgm9fw" />
              <rect x="3" y="14" width="7" height="7" data-id="element-544" data-mp="br9u3m9nbsg1" />
            </svg>
            Deck
          </a>
        </div>
      </header>

      <div className="max-w-[1920px] mx-auto px-4 py-8 md:py-12 pb-16" data-id="element-545" data-mp="1j5lb83jt4o9x">
        {/* Hero Section */}
        <div className="mb-12 md:mb-16 text-center" data-id="element-546" data-mp="1n5ncwcpjlhp7">
          <h1 className="text-4xl md:text-5xl lg:text-[52px] font-bold text-navy leading-tight mb-4 tracking-tight" data-id="element-547" data-mp="1n89kudgchz7y">
            Recruiting ROI Calculator
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-548" data-mp="1grpvpp732deh">
            Enter your business assumptions below to calculate your 12-month ROI
            projection. See exactly how HIRE delivers predictable recruiting
            results.
          </p>
        </div>

        {/* Step 1: Input Form */}
        <div className="mb-8 md:mb-12" data-id="element-549" data-mp="24lr9vtss9wdr">
          <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-550" data-mp="1zpsn2an3smfg">
            <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-551" data-mp="2edx99old7g2z">
              1
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-552" data-mp="29g5b4cu4mxqc">
              Business Assumptions
            </h2>
          </div>
          <CalculatorInputs inputs={inputs} onChange={setInputs} onReset={handleReset} onReview={handleReview} showActionButtons={false} data-id="element-553" data-mp="2nc8wdgn0vvqo" />
        </div>

        {/* Step 2: Validation Summary */}
        {hasReviewed && <div ref={reviewRef} className="mb-8 md:mb-12" data-id="element-554" data-mp="36y1399kq8r87">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-555" data-mp="2szuc0caa9phi">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-556" data-mp="2xz7ajuaiokzx">
                2
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-557" data-mp="2j9hr2iyk3uau">
                Review Your Numbers
              </h2>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded p-6 md:p-8 shadow-lg" data-id="element-558" data-mp="2aao1vdnk267b">
              {/* Hiring Funnel Breakdown Button */}
              <div className="mb-6 flex justify-end gap-3" data-id="element-559" data-mp="2ebzjavw9zl4f">
                <button onClick={handlePlusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-560" data-mp="20boppl3kzsfk">
                  {plusFive ? '✓ ' : ''}+5%
                </button>
                <button onClick={handleToggleOnTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-561" data-mp="23kgj6uuhc2k">
                  {onTarget ? '✓ ' : ''}On Target
                </button>
              </div>

              {/* Monthly Ad Investment & Retention Funnel - Side by Side */}
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-id="element-562" data-mp="fupem4ikdwas">
                {/* Left: Monthly Ad Investment */}
                <div data-id="element-563" data-mp="35kj4vt5bjmmu"></div>

                {/* Right: Retention Funnel Visualization */}
              </div>

              {/* Expandable Hiring Funnel - Conditionally shown */}
              <div className="mb-4" data-id="element-564" data-mp="2qhj8xfzxfnyl">
                {/* Overhead Section - appears ABOVE Recruiting Metrics */}
                {showOverhead && <div className="mb-8" data-id="element-565" data-mp="30enupepv1hev">
                    <div className="relative group" data-id="element-566" data-mp="30dm6s9nqx0fq">
                      <h4 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-567" data-mp="3554av6l2fh4x">
                        Overhead
                        <button onClick={e => {
                    e.stopPropagation();
                    setShowOverhead(false);
                  }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Overhead" data-id="element-568" data-mp="395o624m2waof">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-569" data-mp="1tqh756yzeawx">
                            <polyline points="6 9 12 15 18 9" data-id="element-570" data-mp="4awqdbfy12wb" />
                          </svg>
                          Collapse
                        </button>
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-571" data-mp="3lpbnhfw6rjbi">
                      {/* Total Time Investment */}
                      <div className="p-4 rounded bg-orange-50 border-2 border-orange-200" data-id="element-572" data-mp="3hngnqftwldzu">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-573" data-mp="10yjiix5zhz3y">
                          Total Time Investment
                        </div>
                        <div className="space-y-3" data-id="element-574" data-mp="wx7nud896now">
                          <div className="cursor-pointer hover:bg-orange-100 p-2 rounded transition-colors" data-id="element-575" data-mp="14gqri68u7fpg">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-576" data-mp="19h4omt8wh58p">
                              Pre-Interview
                            </div>
                            <div className="relative" data-id="element-577" data-mp="zh44j537il3g">
                              <input type="number" min="0" step="1" value={preInterviewHours} onChange={e => setPreInterviewHours(parseFloat(e.target.value) || 0)} className="w-full text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-578" data-mp="zjqmlynq4izx" />
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm" data-id="element-579" data-mp="3cw1vppswvmys">
                                hours
                              </span>
                            </div>
                          </div>
                          <div className="cursor-pointer hover:bg-orange-100 p-2 rounded transition-colors" data-id="element-580" data-mp="3rlzl292kuipk">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-581" data-mp="2tyh78ju26qep">
                              Interview-to-Onboarding
                            </div>
                            <div className="relative" data-id="element-582" data-mp="33woluw0vkwdj">
                              <input type="number" min="0" step="1" value={interviewToOnboardingHours} onChange={e => setInterviewToOnboardingHours(parseFloat(e.target.value) || 0)} className="w-full text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-583" data-mp="33v3oyzr391ci" />
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm" data-id="element-584" data-mp="1txdqwx7uh4gq">
                                hours
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Money Value of Time */}
                      <div className="p-4 rounded bg-red-50 border-2 border-red-200" data-id="element-585" data-mp="1azszbidp3rsf">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-586" data-mp="3acn9pq4z1vq1">
                          Money Value of Time
                        </div>
                        <div className="space-y-3" data-id="element-587" data-mp="35evjr1nw0joh">
                          <div className="cursor-pointer hover:bg-red-100 p-2 rounded transition-colors" data-id="element-588" data-mp="13ood45kj8t5z">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-589" data-mp="18ev1q5f1odo8">
                              Admin
                            </div>
                            <div className="relative" data-id="element-590" data-mp="ypbeyoo7nzo3">
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg" data-id="element-591" data-mp="yq3cw65a69hj">
                                $
                              </span>
                              <input type="text" value={adminCost.toLocaleString()} onChange={e => {
                          const numericValue = e.target.value.replace(/,/g, '');
                          setAdminCost(parseFloat(numericValue) || 0);
                        }} className="w-full pl-5 text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-592" data-mp="1rco2p263ydiz" />
                            </div>
                          </div>
                          <div className="cursor-pointer hover:bg-red-100 p-2 rounded transition-colors" data-id="element-593" data-mp="2p7rt6okw0yd1">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-594" data-mp="21hea3nka69ul">
                              Leadership
                            </div>
                            <div className="relative" data-id="element-595" data-mp="2a9zd9vbfi7yb">
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg" data-id="element-596" data-mp="2absolnwzlgcn">
                                $
                              </span>
                              <input type="text" value={leadershipCost.toLocaleString()} onChange={e => {
                          const numericValue = e.target.value.replace(/,/g, '');
                          setLeadershipCost(parseFloat(numericValue) || 0);
                        }} className="w-full pl-5 text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-597" data-mp="1hsdjzw1o3u1n" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                <div className="relative group" data-id="element-598" data-mp="ot0b5uzehybl">
                  {/* Hidden arrow above Recruiting Metrics heading - shows when funnel is fully expanded */}
                  {showHiringMetrics && !showOverhead && <button onClick={() => setShowOverhead(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Overhead" data-id="element-599" data-mp="m8ke3qug3kl4">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-600" data-mp="7gs31r1oosp7">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-601" data-mp="1564vydysliw8">
                          <polyline points="18 15 12 9 6 15" data-id="element-602" data-mp="3az3h8yci6n78" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-603" data-mp="2317gcon2i25a">
                        Click to expand
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-604" data-mp="1t16uow1q79xx">
                    Recruiting Metrics
                    {/* Collapse button for both Overhead + Recruiting Metrics */}
                    {showHiringMetrics && <button onClick={e => {
                  e.stopPropagation();
                  setShowHiringMetrics(false);
                  setShowOverhead(false);
                }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Recruiting Metrics" data-id="element-605" data-mp="1o3f3634ztpu9">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-606" data-mp="lsos0ji9ng48">
                          <polyline points="6 9 12 15 18 9" data-id="element-607" data-mp="14n3oo2h3uwow" />
                        </svg>
                        Collapse
                      </button>}
                  </h3>
                </div>

                {showHiringMetrics && <div className="mb-6" data-id="element-608" data-mp="19hhnkeuz7lyb">
                    <ExpandableHiringFunnel estimatedHires={reverseHires} data-id="element-609" data-mp="19ij2wvj0yulu" />
                  </div>}
              </div>

              {/* Projected Recruiting Metrics */}
              <div className="mb-8" data-id="element-610" data-mp="1jc9o9rjuuoxb">
                <div className="relative group" data-id="element-611" data-mp="42jqm8ayinoy">
                  {/* Hidden arrow above Retention Metrics heading */}
                  {!showHiringMetrics && <button onClick={() => setShowHiringMetrics(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Recruiting Metrics" data-id="element-612" data-mp="92xzfskdu1o1">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-613" data-mp="3mh2peh13jlms">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-614" data-mp="3rekpdhx4yqin">
                          <polyline points="18 15 12 9 6 15" data-id="element-615" data-mp="1ktm6i3rf4smc" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-616" data-mp="1gqzbjtzgarkx">
                        Recruiting Metrics
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-617" data-mp="11vjerh7hkhku">
                    Retention Metrics
                  </h3>
                </div>

                <div className="space-y-3" data-id="element-618" data-mp="x2zygchez8cc">
                  <div className="flex items-center gap-3" data-id="element-619" data-mp="t2g57jzyoovh">
                    <div className="w-20 text-right" data-id="element-620" data-mp="t0vb1vhk77y5">
                      <span className="text-xl font-bold text-navy" data-id="element-621" data-mp="o5fu2uo98vxi">
                        {reverseHires.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-622" data-mp="e5gq7b2s5tqz">
                      Hired
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-623" data-mp="3gzlp17i804sw">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-navy to-navy/90 rounded-full" style={{
                    width: '100%'
                  }} data-id="element-624" data-mp="3c5pyur877m8a" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-625" data-mp="3pemhgv49yju">
                    <div className="w-20 text-right" data-id="element-626" data-mp="3uytswdpbw13f">
                      <span className="text-lg font-bold text-navy" data-id="element-627" data-mp="3uvz5cdb0q05s">
                        {reverseLaunched.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-628" data-mp="3lwudfptyp52l">
                      Launched
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-629" data-mp="2ai6xmruls8e8">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber to-amber/90 rounded-full" style={{
                    width: `${reverseLaunched / reverseHires * 100}%`
                  }} data-id="element-630" data-mp="q069j190ffp3" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-631" data-mp="13scm6dcyf4js">
                      {inputs.pctLaunched}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-632" data-mp="1doz875h0o1ne">
                    <div className="w-20 text-right" data-id="element-633" data-mp="1drlfr53wqh5m">
                      <span className="text-lg font-bold text-navy" data-id="element-634" data-mp="1hqu4two05quu">
                        {reverseActive30.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-635" data-mp="3jip57g4t68xr">
                      Active 30d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-636" data-mp="3oibitqojtr73">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" style={{
                    width: `${reverseActive30 / reverseHires * 100}%`
                  }} data-id="element-637" data-mp="vnuw6wjizsem" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-638" data-mp="zqzvkq8b7dz1">
                      {inputs.pct30}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-639" data-mp="19mbb3arjkp7u">
                    <div className="w-20 text-right" data-id="element-640" data-mp="1ner73rm0zmz9">
                      <span className="text-lg font-bold text-navy" data-id="element-641" data-mp="1ndg61zjystbu">
                        {reverseActive90.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-642" data-mp="1xd70k18iz8bx">
                      Active 90d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-643" data-mp="1sbhm0bm3gcj3">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{
                    width: `${reverseActive90 / reverseHires * 100}%`
                  }} data-id="element-644" data-mp="2bx9rc045gzyi" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-645" data-mp="1t8ct5l5mtnb0">
                      {inputs.pct90}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-646" data-mp="1jaglj3bwlahn">
                    <div className="w-20 text-right" data-id="element-647" data-mp="1jc01stxk7ljf">
                      <span className="text-lg font-bold text-navy" data-id="element-648" data-mp="1o99xgrbjktfp">
                        {targetActive1Year.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-649" data-mp="1ac4oojkg1ggr">
                      Active 1 Year
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-650" data-mp="177458gnvsbir">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{
                    width: `${targetActive1Year / reverseHires * 100}%`
                  }} data-id="element-651" data-mp="12a3z65r178rn" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-652" data-mp="ofl30c1ol1hs">
                      {inputs.pct1y}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Performance and Deal Economics - Side by Side */}
              <div className="mb-8" data-id="element-653" data-mp="9lyfznfmlrtx">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-654" data-mp="28xizpr2dppux">
                  {/* Left Column: Monthly Ad Spent */}
                  <div data-id="element-655" data-mp="23y6h5iijj4y5">
                    <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200 flex flex-col justify-center cursor-pointer hover:border-amber transition-colors" style={{
                  minHeight: '120px'
                }} data-id="element-656" data-mp="23yxzvsr1h870">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-657" data-mp="2l8imjz54csfm">
                        Monthly Ad Spend
                      </div>
                      <div className="relative" data-id="element-658" data-mp="1thw2suzehb29">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl" data-id="element-659" data-mp="1xi6acc2uc9j8">
                          $
                        </span>
                        <input type="text" value={adSpendTotal.toLocaleString()} onChange={e => {
                      const numericValue = e.target.value.replace(/,/g, '');
                      const newTotal = parseFloat(numericValue) || 0;
                      const newAdSpend = inputs.campaigns > 0 ? newTotal / inputs.campaigns : newTotal;
                      handleNumericChange('adSpend', newAdSpend.toString());
                    }} className="w-full pl-6 text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-660" data-mp="22jc3uc4bs0tt" />
                      </div>
                    </div>
                  </div>

                  {/* Middle Column: Ad Cost per Hire */}
                  <div data-id="element-661" data-mp="1rpl1uzmzf1nb">
                    <div className="p-4 md:p-6 rounded bg-blue-50 border-2 border-blue-200 flex flex-col justify-center cursor-pointer hover:border-amber transition-colors" style={{
                  minHeight: '120px'
                }} data-id="element-662" data-mp="1rotf8ebd4e96">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-663" data-mp="7223z8vmailp">
                        Ad Cost per Hire
                      </div>
                      <div className="relative" data-id="element-664" data-mp="3tgbm508yeue1">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl" data-id="element-665" data-mp="l002r5xgg6g3">
                          $
                        </span>
                        <input type="number" min="1" step="10" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full pl-6 text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-666" data-mp="pwqrsir8mcdn" />
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Expected Monthly Hires */}
                  <div data-id="element-667" data-mp="3jessh2v4hml5">
                    <div className="p-4 md:p-6 rounded bg-emerald-50 border-2 border-emerald-200 flex flex-col justify-center" style={{
                  minHeight: '120px'
                }} data-id="element-668" data-mp="3je19qsmmjjca">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-669" data-mp="az0s6sl0ft2t">
                        Expected Monthly Hires
                      </div>
                      <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-670" data-mp="18o974sug7ys">
                        {Math.round(adSpendTotal / inputs.cph)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button onClick={handleCalculate} className="w-full py-4 md:py-5 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-671" data-mp="tuqxaoo8p5kx">
                Calculate My ROI →
              </button>
            </div>
          </div>}

        {/* Step 3: Results */}
        {hasCalculated && results && <div ref={resultsRef} className="scroll-mt-8" data-id="element-672" data-mp="1k7m3zkyygxmj">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-673" data-mp="1k4zxlonswo5w">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-674" data-mp="1p4cjo76ulr7f">
                3
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-675" data-mp="1yw8ixlko7nd8">
                Projected Investment and ROI
              </h2>
            </div>

            <div className="mb-4" data-id="element-676" data-mp="22z55fndc03mg">
              {inputs.pkg && <div className="inline-block bg-amber-light text-amber-dark px-4 py-2 rounded text-sm font-semibold border-2 border-amber/20" data-id="element-677" data-mp="27xgf9y3mme4o">
                  {results.preset.name}
                </div>}
            </div>

            <div className="bg-white border-2 border-gray-200 rounded p-6 md:p-8 shadow-lg mb-6" data-id="element-678" data-mp="3el1lm8jo61b9">
              <ResultsSummary results={results} halfSales={halfSales} doubleAttrition={doubleAttrition} timeframe={timeframe} onOptimizeForTarget={handleOptimizeForTarget} onUndoOptimize={handleUndoOptimize} canUndoOptimize={preOptimizeMonthlyCampaigns !== null} data-id="element-679" data-mp="30pheeadaz7jo" />
            </div>

            <div className="mb-6" data-id="element-680" data-mp="3jkegngkp2eox">
              <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} plusFive={plusFive} onPlusFive={handlePlusFive} timeframe={timeframe} setTimeframe={setTimeframe} data-id="element-681" data-mp="3okk2gg4a96z3" />
            </div>

            <div className="mb-6" data-id="element-682" data-mp="2aennca67ckvz">
              <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} plusFive={plusFive} onPlusFive={handlePlusFive} data-id="element-683" data-mp="2afgw1goq1kgr" />
            </div>

            <div className="flex flex-wrap gap-3 mb-6" data-id="element-684" data-mp="375g5reglyp0x">
              <button onClick={handleCopySummary} className="px-3 py-2 rounded border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-685" data-mp="323ihu6x9j61n">
                {copySummaryLabel}
              </button>

              <button onClick={handleReset} className="px-3 py-2 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-686" data-mp="28sbvzv15ksz4">
                Start Over
              </button>
            </div>

            <div className="mb-6" data-id="element-687" data-mp="1l0569epxnk8o">
              <ShareSummary inputs={inputs} results={results} data-id="element-688" data-mp="1q1kpd18jvgof" />
            </div>
          </div>}
      </div>

      {/* Hiring Funnel Modal */}
      <HiringFunnelModal isOpen={showHiringFunnel} onClose={() => setShowHiringFunnel(false)} estimatedHires={reverseHires} data-id="element-689" data-mp="1utc8wrtagm52" />
    </div>;
}